import React, { useState, useEffect } from 'react';
import { Container, Navbar, Nav, Badge, Modal } from 'react-bootstrap';
import APIHandler from '../utils/APIHandler';
import { useNavigate } from 'react-router-dom';

function WorkflowsPage() {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [selectedWf, setSelectedWf] = useState(null);
    const [editJson, setEditJson] = useState('');
    const [showEdit, setShowEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [user, setUser] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const u = JSON.parse(userData);
            setUser(u);
            if (u.role !== 'superadmin') navigate('/dashboard');
        }
        loadWorkflows();
    }, [navigate]);

    const loadWorkflows = async () => {
        setLoading(true);
        try {
            const resp = await APIHandler.makeRequest('get', '/workflows');
            if (resp.Success) setWorkflows(resp.workflows || []);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const viewWorkflow = async (id) => {
        try {
            const resp = await APIHandler.makeRequest('get', `/workflows/${id}`);
            if (resp.Success) {
                setSelectedWf(resp.workflow);
                setEditJson(JSON.stringify(resp.workflow, null, 2));
            }
        } catch (e) { console.error(e); }
    };

    const deleteWorkflow = async (id) => {
        if (!window.confirm(`Delete workflow "${id}"?`)) return;
        try {
            const resp = await APIHandler.makeRequest('delete', `/workflows/${id}`);
            if (resp.Success) {
                setMessage(`Deleted: ${id}`);
                setSelectedWf(null);
                loadWorkflows();
            } else {
                setMessage(resp.Message || 'Delete failed');
            }
        } catch (e) { setMessage('Error: ' + e.message); }
    };

    const saveEdit = async () => {
        try {
            const wfData = JSON.parse(editJson);
            const resp = await APIHandler.makeRequest('post', '/workflows', wfData);
            if (resp.Success) {
                setMessage('Workflow saved!');
                setShowEdit(false);
                setSelectedWf(null);
                loadWorkflows();
            } else {
                setMessage(resp.Message || 'Save failed');
            }
        } catch (e) {
            setMessage('Invalid JSON: ' + e.message);
        }
    };

    const toolIcon = (tool) => {
        const icons = {
            search_student: 'search', get_student_info: 'person', find_devices_by_student: 'phonelink',
            get_chrome_policies: 'policy', get_recent_device_users: 'history', get_device_info: 'laptop',
            filter_users: 'filter_list', evaluate: 'check_circle',
        };
        return icons[tool] || 'settings';
    };

    return (
        <div style={styles.page}>
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

            <Navbar style={styles.navbar}>
                <Container fluid>
                    <Navbar.Brand style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600 }}>
                        <span className="material-icons" style={{ fontSize: 20, marginRight: 8, verticalAlign: 'middle' }}>account_tree</span>
                        Workflows
                    </Navbar.Brand>
                    <Nav>
                        <Nav.Link onClick={() => navigate('/dashboard')} style={{ color: '#94a3b8' }}>
                            <span className="material-icons" style={{ fontSize: 18 }}>chat</span> Chat
                        </Nav.Link>
                        <Nav.Link onClick={() => navigate('/admin')} style={{ color: '#94a3b8' }}>
                            <span className="material-icons" style={{ fontSize: 18 }}>admin_panel_settings</span> Users
                        </Nav.Link>
                    </Nav>
                </Container>
            </Navbar>

            <div style={styles.content}>
                {message && (
                    <div style={styles.toast} onClick={() => setMessage('')}>
                        {message} <span style={{ opacity: 0.5, marginLeft: 8 }}>click to dismiss</span>
                    </div>
                )}

                <div style={styles.grid}>
                    {/* Workflow list */}
                    <div style={styles.listPanel}>
                        <div style={styles.listHeader}>
                            <h3 style={{ margin: 0, fontSize: 15 }}>Saved Workflows ({workflows.length})</h3>
                        </div>
                        {loading && <div style={styles.empty}>Loading...</div>}
                        {!loading && workflows.length === 0 && (
                            <div style={styles.empty}>No workflows yet. Ask the chat to create one!</div>
                        )}
                        {workflows.map(wf => (
                            <div key={wf.id} onClick={() => viewWorkflow(wf.id)}
                                style={{ ...styles.listItem, ...(selectedWf?.id === wf.id ? styles.listItemActive : {}) }}>
                                <div style={styles.listItemHeader}>
                                    <span className="material-icons" style={{ fontSize: 18, color: '#3b82f6' }}>account_tree</span>
                                    <strong style={{ fontSize: 13 }}>{wf.name || wf.id}</strong>
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{wf.description?.slice(0, 80)}</div>
                                <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                                    <Badge bg="secondary" style={{ fontSize: 10 }}>{wf.step_count} steps</Badge>
                                    {wf.parameters?.map((p, i) => (
                                        <Badge key={i} bg="info" style={{ fontSize: 10 }}>{p.name}</Badge>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Workflow detail */}
                    <div style={styles.detailPanel}>
                        {!selectedWf && (
                            <div style={styles.emptyDetail}>
                                <span className="material-icons" style={{ fontSize: 48, color: '#1e293b' }}>account_tree</span>
                                <p>Select a workflow to view details</p>
                            </div>
                        )}
                        {selectedWf && (
                            <div>
                                <div style={styles.detailHeader}>
                                    <div>
                                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{selectedWf.name}</h2>
                                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{selectedWf.description}</p>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button onClick={() => setShowEdit(true)} style={styles.editBtn}>
                                            <span className="material-icons" style={{ fontSize: 16 }}>edit</span> Edit
                                        </button>
                                        <button onClick={() => deleteWorkflow(selectedWf.id)} style={styles.deleteBtn}>
                                            <span className="material-icons" style={{ fontSize: 16 }}>delete</span>
                                        </button>
                                    </div>
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Parameters</h4>
                                    {selectedWf.parameters?.length > 0 ? selectedWf.parameters.map((p, i) => (
                                        <div key={i} style={styles.paramItem}>
                                            <Badge bg="info">{p.type || 'string'}</Badge>
                                            <strong>{p.name}</strong>
                                            <span style={{ color: '#64748b' }}>{p.description}</span>
                                        </div>
                                    )) : <div style={{ fontSize: 13, color: '#475569' }}>No parameters</div>}
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Steps</h4>
                                    {selectedWf.steps?.map((step, i) => (
                                        <div key={i} style={styles.stepItem}>
                                            <div style={styles.stepNumber}>{i + 1}</div>
                                            <div style={styles.stepContent}>
                                                <div style={styles.stepHeader}>
                                                    <span className="material-icons" style={{ fontSize: 16, color: '#3b82f6' }}>
                                                        {toolIcon(step.tool || step.type)}
                                                    </span>
                                                    <strong>{step.tool || step.type}</strong>
                                                    <span style={{ fontSize: 11, color: '#475569' }}>({step.id})</span>
                                                </div>
                                                {step.args && (
                                                    <div style={styles.stepArgs}>
                                                        {Object.entries(step.args).map(([k, v]) => (
                                                            <span key={k} style={styles.argChip}>
                                                                {k}: <code style={{ color: '#60a5fa' }}>{v}</code>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {step.extract && (
                                                    <div style={{ fontSize: 11, color: '#22c55e', marginTop: 4 }}>
                                                        Extracts: {Object.entries(step.extract).map(([k, v]) => `${k} ← ${v}`).join(', ')}
                                                    </div>
                                                )}
                                                {step.condition && (
                                                    <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>
                                                        Condition: {step.condition}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={styles.section}>
                                    <h4 style={styles.sectionTitle}>Metadata</h4>
                                    <div style={{ fontSize: 12, color: '#475569' }}>
                                        Created by: {selectedWf.created_by || 'unknown'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Edit Modal */}
                {showEdit && (
                    <div style={styles.modal}>
                        <div style={styles.modalContent}>
                            <div style={styles.modalHeader}>
                                <h3 style={{ margin: 0, fontSize: 16 }}>Edit Workflow JSON</h3>
                                <button onClick={() => setShowEdit(false)} style={styles.closeBtn}>
                                    <span className="material-icons">close</span>
                                </button>
                            </div>
                            <textarea
                                value={editJson}
                                onChange={e => setEditJson(e.target.value)}
                                style={styles.editor}
                                spellCheck={false}
                            />
                            <div style={styles.modalFooter}>
                                <button onClick={() => setShowEdit(false)} style={styles.cancelBtn}>Cancel</button>
                                <button onClick={saveEdit} style={styles.saveBtn}>
                                    <span className="material-icons" style={{ fontSize: 16 }}>save</span> Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: { minHeight: '100vh', backgroundColor: '#0a0f1a', fontFamily: "'Inter', sans-serif", color: '#e2e8f0' },
    navbar: { borderBottom: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(15,23,42,0.8)', padding: '8px 0' },
    content: { maxWidth: 1200, margin: '0 auto', padding: '20px 16px' },
    toast: { padding: '10px 16px', borderRadius: 8, backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontSize: 13, marginBottom: 16, cursor: 'pointer' },
    grid: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, minHeight: 'calc(100vh - 120px)' },
    listPanel: { backgroundColor: '#0d1321', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' },
    listHeader: { padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    listItem: {
        padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)',
        transition: 'background 0.15s',
    },
    listItemActive: { backgroundColor: 'rgba(59,130,246,0.1)', borderLeft: '3px solid #3b82f6' },
    listItemHeader: { display: 'flex', alignItems: 'center', gap: 8 },
    empty: { padding: 24, textAlign: 'center', color: '#475569', fontSize: 13 },
    detailPanel: { backgroundColor: '#151d2e', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', padding: 24 },
    emptyDetail: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: '#334155' },
    detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' },
    editBtn: {
        display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 8,
        backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
        color: '#60a5fa', fontSize: 12, cursor: 'pointer',
    },
    deleteBtn: {
        display: 'flex', alignItems: 'center', padding: '6px 10px', borderRadius: 8,
        backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
        color: '#f87171', fontSize: 12, cursor: 'pointer',
    },
    section: { marginBottom: 20 },
    sectionTitle: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
    paramItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 },
    stepItem: { display: 'flex', gap: 12, marginBottom: 12 },
    stepNumber: {
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, border: '1px solid rgba(59,130,246,0.3)',
    },
    stepContent: { flex: 1, padding: '4px 0' },
    stepHeader: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
    stepArgs: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
    argChip: {
        fontSize: 11, padding: '2px 8px', borderRadius: 6,
        backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
    },
    modal: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: '#151d2e', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)',
        width: '80%', maxWidth: 800, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
    },
    modalHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    closeBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' },
    editor: {
        flex: 1, margin: 16, padding: 16, borderRadius: 8,
        backgroundColor: '#0a0f1a', border: '1px solid rgba(255,255,255,0.08)',
        color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12, resize: 'none',
        minHeight: 400, outline: 'none',
    },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' },
    cancelBtn: {
        padding: '8px 16px', borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: 13, cursor: 'pointer',
    },
    saveBtn: {
        display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', borderRadius: 8,
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none',
        color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
};

export default WorkflowsPage;
